'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Sparkles, Play, ChevronRight } from 'lucide-react';

const campusModes = [
  { id: 'kid', label: '🌟 Kid Mode', desc: 'Ages 6–9 · Bright & Playful', color: 'border-green-300 bg-green-50 text-green-700' },
  { id: 'teen', label: '🚀 Teen Mode', desc: 'Ages 10–17 · Exploratory', color: 'border-blue-300 bg-blue-50 text-blue-700' },
  { id: 'pro', label: '🎓 Pro Mode', desc: 'Ages 18+ · Advanced', color: 'border-primary/30 bg-primary/5 text-primary' },
];

export default function HomeHero() {
  const [activeMode, setActiveMode] = useState('pro');

  return (
    <section className="relative overflow-hidden campus-hero-gradient">
      {/* Decorative blobs */}
      <div className="absolute top-20 left-10 w-72 h-72 rounded-full opacity-20 blur-3xl bg-primary pointer-events-none" />
      <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full opacity-10 blur-3xl bg-accent pointer-events-none" />
      <div className="absolute top-40 right-40 w-48 h-48 rounded-full opacity-15 blur-2xl bg-secondary pointer-events-none" />
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left — Copy */}
          <div className="fade-in">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs font-semibold mb-6 border border-primary/20">
              <Sparkles size={12} />
              The World's First AI-Guided Living Campus
            </div>

            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-extrabold text-foreground leading-tight mb-6">
              Where Every{' '}
              <span className="text-primary">Mind</span>{' '}
              Grows
            </h1>

            <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl">
              Jarvondis University is a living digital campus built around the Tree of Life — guiding students from age 6 to professional mastery with AI tutors that adapt to every learner.
            </p>

            {/* Campus mode selector */}
            <div className="flex flex-wrap gap-3 mb-10">
              {campusModes?.map((m) => (
                <button
                  key={`mode-${m?.id}`}
                  onClick={() => setActiveMode(m?.id)}
                  className={`flex flex-col items-start px-4 py-3 rounded-xl border-2 transition-all duration-200 btn-scale ${
                    activeMode === m?.id ? m?.color + ' shadow-sm' : 'border-border bg-card text-muted-foreground hover:border-border/80'
                  }`}
                >
                  <span className="text-sm font-semibold">{m?.label}</span>
                  <span className="text-xs opacity-70 mt-0.5">{m?.desc}</span>
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <Link
                href="/sign-up-login-screen"
                className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-semibold rounded-xl btn-scale hover:bg-primary/90 transition-colors shadow-sm"
              >
                Enter Campus <ChevronRight size={16} />
              </Link>
              <Link
                href="/course-catalog"
                className="flex items-center gap-2 px-6 py-3 bg-card border border-border text-foreground font-semibold rounded-xl btn-scale hover:bg-muted transition-colors"
              >
                <Play size={16} className="text-primary" />
                Explore Courses
              </Link>
            </div>
          </div>

          {/* Right — Campus scene */}
          <div className="relative flex justify-center items-center">
            <div className="relative w-80 h-80 lg:w-96 lg:h-96">
              {/* Tree of Life SVG */}
              <svg viewBox="0 0 320 320" className="w-full h-full tree-sway" aria-label="Tree of Life — Jarvondis University campus symbol">
                {/* Ground */}
                <ellipse cx="160" cy="290" rx="80" ry="12" fill="#dcfce7" />
                {/* Trunk */}
                <path d="M155 280 Q150 220 148 180 Q146 140 155 100 Q160 80 165 100 Q174 140 172 180 Q170 220 165 280 Z" fill="#92400e" opacity="0.7" />
                {/* Spiral path around trunk */}
                <path d="M160 280 Q130 250 120 210 Q110 170 130 140 Q150 110 170 90" stroke="#f59e0b" strokeWidth="3" fill="none" strokeDasharray="6 4" opacity="0.6" />
                {/* Main canopy */}
                <circle cx="160" cy="90" r="70" fill="#16a34a" opacity="0.15" />
                <circle cx="160" cy="90" r="55" fill="#16a34a" opacity="0.25" />
                <circle cx="160" cy="90" r="40" fill="#16a34a" opacity="0.5" />
                {/* Branches */}
                <path d="M160 130 Q130 100 110 70" stroke="#166534" strokeWidth="4" fill="none" strokeLinecap="round" />
                <path d="M160 130 Q190 100 210 70" stroke="#166534" strokeWidth="4" fill="none" strokeLinecap="round" />
                <path d="M160 110 Q160 80 160 50" stroke="#166534" strokeWidth="5" fill="none" strokeLinecap="round" />
                {/* Leaves */}
                <circle cx="110" cy="70" r="18" fill="#22c55e" opacity="0.8" />
                <circle cx="210" cy="70" r="18" fill="#22c55e" opacity="0.8" />
                <circle cx="160" cy="45" r="22" fill="#16a34a" />
                {/* Fountain ring */}
                <circle cx="160" cy="280" r="60" fill="none" stroke="#0ea5e9" strokeWidth="2" strokeDasharray="8 4" opacity="0.5" className="fountain-spin" style={{ transformOrigin: '160px 280px' }} />
                {/* Bench */}
                <rect x="85" y="270" width="40" height="5" rx="2" fill="#78350f" />
                <rect x="90" y="258" width="3" height="14" rx="1" fill="#78350f" />
                <rect x="119" y="258" width="3" height="14" rx="1" fill="#78350f" />
                {/* Digital son (simple figure) */}
                <circle cx="105" cy="252" r="7" fill="#f59e0b" />
                <path d="M100 260 Q105 275 110 260" stroke="#d97706" strokeWidth="2" fill="none" />
                {/* Stars */}
                <circle cx="70" cy="50" r="3" fill="#f59e0b" opacity="0.7" />
                <circle cx="250" cy="40" r="2" fill="#f59e0b" opacity="0.5" />
                <circle cx="240" cy="150" r="2.5" fill="#0ea5e9" opacity="0.6" />
                <circle cx="80" cy="160" r="2" fill="#0ea5e9" opacity="0.4" />
              </svg>

              {/* Floating info cards */}
              <div className="absolute -top-4 -right-4 bg-card border border-border rounded-xl px-3 py-2 shadow-card fade-in">
                <p className="text-xs text-muted-foreground">Active Learners</p>
                <p className="text-lg font-bold text-foreground font-tabular">48,291</p>
              </div>
              <div className="absolute -bottom-2 -left-4 bg-card border border-border rounded-xl px-3 py-2 shadow-card fade-in">
                <p className="text-xs text-muted-foreground">Courses Available</p>
                <p className="text-lg font-bold text-primary font-tabular">340+</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}